import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BoxedImage, Crop } from '../../../components/BoxedImage';
import { ProductRecognitionBBox, ProductRecognitionPolygon } from '../../../types';
import { getConfidenceClass, mapOcrConfidenceToLabel } from '../../../utils/confidenceUtils';

interface ProductRecognitionDetectionContentProps {
  item: ProductRecognitionBBox | ProductRecognitionPolygon;
  cameraImageElement?: HTMLImageElement;
  layerBboxColor?: string;
  chainId?: number;
  storeId?: number;
  onCandidateSelected?: (candidate: any, index: number) => void;
}

export const ProductRecognitionDetectionContent: React.FC<
  ProductRecognitionDetectionContentProps
> = ({ item, cameraImageElement, layerBboxColor, chainId, storeId, onCandidateSelected }) => {
  const [zoomedImage, setZoomedImage] = useState<HTMLImageElement | null>(null);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState<number | null>(null);

  // Use the provided HTMLImageElement directly
  const cropImage = cameraImageElement;

  // Use pre-calculated crop coordinates from item.shape
  const cropCoordinates = useMemo((): Crop | undefined => {
    if (!cropImage || !item.shape) return undefined;
    return item.shape;
  }, [item.shape, cropImage]);

  return (
    <>
      {/* First Row: Crop Image + Details */}
      <div className="flex gap-4 mb-4">
        {/* Left: Cropped Camera Image */}
        <div className="flex-shrink-0 w-48">
          {cropImage && cropCoordinates && (
            <div>
              <span className="text-gray-600 block mb-2 text-xs">Detected Area:</span>
              <div
                className="w-full overflow-hidden rounded border cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => cameraImageElement && setZoomedImage(cameraImageElement)}
              >
                <BoxedImage
                  image={cropImage}
                  crop={cropCoordinates}
                  cropPadding={20}
                  boxes={[
                    {
                      shape: cropCoordinates,
                      color: layerBboxColor || '#3b82f6',
                      isPolygon: 'points' in item,
                      strokeWidth: 5,
                      points: 'points' in item ? item.points : undefined,
                    },
                  ]}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right: Details */}
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-2 gap-2">
            {'x1' in item && (
              <>
                <span className="text-gray-600">Coordinates:</span>
                <span className="font-mono">
                  ({item.x1}, {item.y1}) - ({item.x2}, {item.y2})
                </span>
              </>
            )}
            {'points' in item && (
              <>
                <span className="text-gray-600">Points:</span>
                <span className="font-mono">{item.points.length} vertices</span>
              </>
            )}
            {item.type && (
              <>
                <span className="text-gray-600">Type:</span>
                <span>{item.type}</span>
              </>
            )}
            {item.confidence && (
              <>
                <span className="text-gray-600">Confidence:</span>
                <span>{(item.confidence * 100).toFixed(1)}%</span>
              </>
            )}
            {item.column !== undefined && (
              <>
                <span className="text-gray-600">Column:</span>
                <span>{item.column}</span>
              </>
            )}
            {item.mod !== undefined && (
              <>
                <span className="text-gray-600">Mod:</span>
                <span>{item.mod}</span>
              </>
            )}
            {item.shelf !== undefined && (
              <>
                <span className="text-gray-600">Shelf:</span>
                <span>{item.shelf}</span>
              </>
            )}
            {item.ocr?.text && (
              <>
                <span className="text-gray-600">OCR Text:</span>
                <span>"{item.ocr.text}"</span>
              </>
            )}
            {item.ocr?.confidence !== undefined && (
              <>
                <span className="text-gray-600">OCR Confidence:</span>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${getConfidenceClass(
                    item.ocr.confidence
                  )}`}
                >
                  {mapOcrConfidenceToLabel(item.ocr.confidence)}
                </span>
              </>
            )}
            {'finalUpc' in item && typeof item.finalUpc === 'string' && (
              <>
                <span className="text-gray-600">Final UPC:</span>
                <span>{item.finalUpc}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Second Row: Similar Products */}
      {item.vizSimCandidates && item.vizSimCandidates.length > 0 && (
        <div>
          <span className="text-gray-600 block mb-3 font-medium">Similar Products:</span>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {item.vizSimCandidates.slice(0, 10).map((candidate, idx) => (
              <div
                key={idx}
                onClick={() => {
                  if (onCandidateSelected) {
                    setSelectedCandidateIndex(idx);
                    onCandidateSelected(candidate, idx);
                  }
                }}
                className={`flex-shrink-0 w-56 p-3 rounded border text-xs transition-colors ${
                  onCandidateSelected ? 'cursor-pointer hover:shadow-md' : ''
                } ${
                  selectedCandidateIndex === idx
                    ? 'bg-blue-50 border-blue-300'
                    : candidate.isConfident
                      ? 'bg-green-50 border-green-200'
                      : 'bg-white border-gray-200'
                }`}
              >
                {/* Product Image - Larger and clickable */}
                <div className="mb-2">
                  {candidate.productImageUri ? (
                    <img
                      src={candidate.productImageUri}
                      alt={candidate.productName || candidate.normalizedUpc}
                      className="w-full h-48 object-contain rounded cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (candidate.productImageUri) {
                          const img = new Image();
                          img.src = candidate.productImageUri;
                          img.onload = () => setZoomedImage(img);
                        }
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-24 bg-gray-200 rounded flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div>
                  {/* Product Name - Bolded and wrapped */}
                  <div className="font-bold text-gray-900 text-xs leading-tight mb-1 line-clamp-2">
                    {candidate.productName || 'Unknown Product'}
                  </div>

                  {/* Normalized UPC */}
                  <div className="text-gray-600 font-mono text-xs mb-1 truncate">
                    {candidate.normalizedUpc}
                  </div>

                  {/* Item Number and Product ID on single line */}
                  {(candidate.itemNumber || candidate.productId) && (
                    <div className="text-gray-500 text-xs mb-1 truncate">
                      {candidate.itemNumber && `Item: ${candidate.itemNumber}`}
                      {candidate.itemNumber && candidate.productId && ' • '}
                      {candidate.productId && `ID: ${candidate.productId}`}
                    </div>
                  )}

                  {/* Similarity Level and Embeddings Link */}
                  <div className="flex items-center justify-between">
                    <div
                      className={`text-xs flex items-center gap-1 ${
                        candidate.isConfident ? 'text-green-700' : 'text-gray-600'
                      }`}
                    >
                      <span className="font-medium">
                        {(candidate.similarity * 100).toFixed(1)}%
                      </span>
                      {candidate.isConfident && (
                        <svg
                          className="w-3 h-3 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>

                    {/* Embeddings Link */}
                    {chainId &&
                      candidate.normalizedUpc &&
                      (() => {
                        const params = new URLSearchParams();
                        params.append('chain_id', chainId.toString());
                        if (storeId) {
                          params.append('store_id', storeId.toString());
                        }
                        params.append('upc', candidate.normalizedUpc);
                        return (
                          <Link
                            to={`/embeddings?${params.toString()}`}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            title="View embeddings for this UPC"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                              />
                            </svg>
                            <span>Embeddings</span>
                          </Link>
                        );
                      })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {zoomedImage === cameraImageElement && cropCoordinates ? (
              // Show camera image with detected box highlighted
              (() => {
                const maxWidth = window.innerWidth * 0.9;
                const maxHeight = window.innerHeight * 0.9;
                const imageAspectRatio = zoomedImage.naturalWidth / zoomedImage.naturalHeight;
                const containerAspectRatio = maxWidth / maxHeight;

                let displayWidth, displayHeight;
                if (imageAspectRatio > containerAspectRatio) {
                  // Image is wider than container
                  displayWidth = Math.min(maxWidth, zoomedImage.naturalWidth);
                  displayHeight = displayWidth / imageAspectRatio;
                } else {
                  // Image is taller than container
                  displayHeight = Math.min(maxHeight, zoomedImage.naturalHeight);
                  displayWidth = displayHeight * imageAspectRatio;
                }

                return (
                  <div
                    className="bg-white rounded shadow-lg"
                    style={{
                      width: `${displayWidth}px`,
                      height: `${displayHeight}px`,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <BoxedImage
                      image={zoomedImage}
                      boxes={[
                        {
                          shape: cropCoordinates,
                          color: layerBboxColor || '#3b82f6',
                          strokeWidth: 10,
                          isPolygon: 'points' in item,
                          points: 'points' in item ? item.points : undefined,
                        },
                      ]}
                    />
                  </div>
                );
              })()
            ) : (
              // Show regular product image
              <img
                src={zoomedImage.src}
                alt="Zoomed product"
                className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-lg"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 transition-all shadow-lg"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
